import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { BadgeCheck, ChevronRight, Filter, MapPin, MessageSquare, Search, Sparkles, Star, Zap } from "lucide-react";
import { TALENT, idr } from "../data/kabelul";

export default function TalentPage({ setPage }) {
  const [query, setQuery] = useState("");
  const [location, setLocation] = useState("All");
  const [tier, setTier] = useState("All");

  const filtered = useMemo(() => {
    return TALENT.filter((t) => {
      if (query && !`${t.name} ${t.title} ${t.skills.join(" ")}`.toLowerCase().includes(query.toLowerCase())) return false;
      if (location !== "All" && t.location !== location) return false;
      if (tier !== "All" && t.tier !== tier) return false;
      return true;
    });
  }, [query, location, tier]);

  const locations = ["All", ...Array.from(new Set(TALENT.map((t) => t.location)))];
  const tiers = ["All", ...Array.from(new Set(TALENT.map((t) => t.tier)))];

  return (
    <div className="page-inner">
      <div className="k-context-pill" data-testid="business-context-pill"><Sparkles size={11} /> Business Workspace · For Businesses</div>
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="page-intro">
        <div>
          <p className="eyebrow">FOR BUSINESSES · DISCOVER TALENT</p>
          <h1>Verified Indonesian technicians</h1>
          <p className="subtitle">Every profile is backed by KABELUL-verified certifications and reputation.</p>
        </div>
        <div className="k-talent-hero">
          <Sparkles size={17} />
          <div>
            <b>3,412 verified professionals</b>
            <small>Filtered by skill, location, and rating — matched by AI.</small>
          </div>
        </div>
      </motion.div>

      <div className="k-toolbar">
        <div className="k-search">
          <Search size={15} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search skill, name, or specialization"
            data-testid="talent-search-input"
          />
        </div>
        <select className="k-select" value={location} onChange={(e) => setLocation(e.target.value)} data-testid="talent-location-filter">
          {locations.map((l) => <option key={l}>{l}</option>)}
        </select>
        <select className="k-select" value={tier} onChange={(e) => setTier(e.target.value)} data-testid="talent-tier-filter">
          {tiers.map((t) => <option key={t}>{t}</option>)}
        </select>
        <button className="secondary-button" onClick={() => toast.info("More filters coming soon")} data-testid="talent-more-filters">
          <Filter size={14} /> More
        </button>
      </div>

      <div className="k-talent-grid">
        {filtered.map((t, i) => (
          <div className={`k-talent-card ${t.isSelf ? "self" : ""}`} key={t.id} data-testid={`talent-card-${i}`}>
            {t.isSelf && <span className="k-self-flag">You</span>}
            <div className="k-talent-head">
              <img src={t.photo} alt={t.name} className="k-avatar-md" />
              <div>
                <b>{t.name} {t.verified && <BadgeCheck size={14} className="verified" />}</b>
                <small>{t.title}</small>
              </div>
              <div className="k-match-pill">
                <Zap size={11} /> {t.matchScore}%
              </div>
            </div>

            <div className="k-talent-meta">
              <span><MapPin size={11} /> {t.location}</span>
              <span><Star size={11} className="star" /> {t.rating} ({t.reviews})</span>
              <span>{t.completed} jobs</span>
            </div>

            <div className="k-chip-row" style={{ marginTop: 10 }}>
              {t.skills.map((s) => <span className="k-chip ok" key={s}>{s}</span>)}
            </div>

            <div className="k-talent-foot">
              <div>
                <small>RATE</small>
                <b>{idr(t.rate)}<span>/hour</span></b>
              </div>
              <div>
                <small>AVAILABILITY</small>
                <b>{t.availability}</b>
              </div>
            </div>

            <div className="k-talent-actions">
              <button className="secondary-button" onClick={() => toast.success(`Invited ${t.name.split(" ")[0]} to your project`)} data-testid={`talent-invite-${i}`}>
                <MessageSquare size={13} /> Invite
              </button>
              <button className="primary-button" onClick={() => setPage("qualifications")} data-testid={`talent-view-${i}`}>
                View passport <ChevronRight size={13} />
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="k-eco-note">
        <Sparkles size={15} />
        <p>Once a job is completed, the technician's reputation updates automatically — <b>Learn → Qualify → Work → Grow</b>.</p>
      </div>
    </div>
  );
}
